import { useCallback, useEffect, useRef, useState } from "react";
import { endpoints } from "../../configs/Settings";
import { useAuth } from "../../utils/useAuth";
import { toast } from "react-toastify";
import { useSessionContext } from "@/contexts/SessionContext";
import { oids } from "@/configs/Settings";
import { isQuestionnaireTask } from "src-next/modules/questionnaire-builder/questionnaire-tasks";

interface TaskWatchingServiceProps {
  resourceType: "Patient" | "PractitionerRole" | "Organization";
  resourceId: string;
}

// Hook React-first para consumir SSE via fetch streaming (permite Authorization header)
// - Sem singletons ou estado global: cada consumidor controla seu ciclo de vida
// - Limpeza garantida no unmount (AbortController)
// - Estados próprios do React para tasks, erro e conexão
export function useTaskWatchingService({
  resourceType,
  resourceId,
}: TaskWatchingServiceProps) {
  const { access, user } = useSessionContext();

  // Estados expostos ao consumidor do hook
  const [tasks, setTasks] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  // Refs para controlar a conexão atual e evitar condições de corrida
  const controllerRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(
    null,
  );
  const reloadTokenRef = useRef<number>(0); // usado para forçar reconnect manual

  // Parseia um bloco SSE e retorna o payload (string) do(s) campos data:
  const extractDataPayload = (rawEvent: string) => {
    const dataLines = rawEvent
      .split("\n")
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.replace(/^data:\s?/, ""));
    if (dataLines.length === 0) return null;
    return dataLines.join("\n");
  };

  // Conecta ao endpoint SSE usando fetch streaming e atualiza estados React
  const connect = useCallback(async () => {
    // Evita conexões com parâmetros inválidos
    if (!resourceType || !resourceId) return;

    // Garante que qualquer conexão anterior seja encerrada antes de abrir outra
    if (controllerRef.current) {
      try {
        controllerRef.current.abort();
      } catch {}
      controllerRef.current = null;
    }
    if (readerRef.current) {
      try {
        await readerRef.current.cancel();
      } catch {}
      readerRef.current = null;
    }

    // Sempre redefinimos estado ao iniciar nova conexão para evitar "vazamento"
    // de tasks entre organizações/visões distintas.
    setIsConnected(false);
    setError(null);
    setTasks([]);

    try {
      // Recupera token com o mesmo método usado no restante do app
      const token = await useAuth.getUserToken();
      if (!token) {
        setError("Token de usuário indisponível");
        setIsConnected(false);
        return;
      }

      const url = `${endpoints.XDSB_domain}/streaming/${resourceType}/${resourceId}`;
      const headers: Record<string, string> = {
        Accept: "text/event-stream",
        Authorization: token,
        "Content-Type": "application/fhir+json",
      };

      const controller = new AbortController();
      controllerRef.current = controller;

      const response = await fetch(url, {
        method: "GET",
        headers,
        signal: controller.signal,
        credentials: "include",
      });

      if (!response.ok || !response.body) {
        setError(`Falha no streaming: ${response.status}`);
        setIsConnected(false);
        return;
      }

      setIsConnected(true);

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      // Loop de leitura do stream SSE
      while (true) {
        const { value, done } = await reader.read();
        if (done) break; // fim do stream
        buffer += decoder.decode(value, { stream: true });

        // Eventos SSE são separados por duas quebras de linha
        let sepIndex: number;
        while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, sepIndex);
          buffer = buffer.slice(sepIndex + 2);

          const payload = extractDataPayload(rawEvent);
          if (!payload) continue;

          try {
            const data = JSON.parse(payload);

            // Atualiza lista de tasks de forma imutável e previsível
            setTasks((prev) => {
              const exists = prev.some((t: any) => t?.id === data?.id);
              return exists
                ? prev.map((t: any) =>
                    t?.id === data?.id ? { ...t, ...data } : t,
                  )
                : [...prev, data];
            });

            // Lógica de toast baseada no tipo de acesso e referência do recurso
            const forRef: string | undefined =
              data?.for?.reference || data?.["for"]?.reference;
            let shouldToast = true;
            if (forRef) {
              if (access?.type === "patient") {
                const myPatientRef = `Patient/${oids.cpf}-${user?.username}`;
                shouldToast = forRef === myPatientRef;
              } else if (access?.type === "professional") {
                const practitionerRoleRef =
                  access?.organizationId && user?.username
                    ? `PractitionerRole/${access.organizationId}-${oids.cpf}-${user.username}`
                    : "";
                const isQuestionnaireFromPatient =
                  !!practitionerRoleRef &&
                  forRef.startsWith("Patient/") &&
                  data?.requester?.reference === practitionerRoleRef &&
                  isQuestionnaireTask(data);
                // Inclui atualizações de questionário enviado ao paciente (ex.: marcado como respondido).
                shouldToast =
                  !forRef.startsWith("Patient/") || isQuestionnaireFromPatient;
              }
            }
            if (shouldToast) toast.info("Nova notificação recebida");
          } catch (err) {
            // Caso alguma mensagem não seja JSON válido
            // Mantemos o stream ativo e apenas registramos em console
            console.warn("[SSE] Evento não-JSON:", payload);
          }
        }
      }

      // Se chegamos aqui, a conexão foi encerrada pelo servidor
      setIsConnected(false);
    } catch (e: any) {
      if (e?.name === "AbortError") {
        // Abortamos propositalmente na limpeza ou refresh
        return;
      }
      console.error("[SSE] Falha ao iniciar/consumir streaming:", e);
      setError("Falha ao iniciar streaming");
      setIsConnected(false);
    }
    // Dependências: reconecta quando tipo, id ou sinalizador de reload muda
  }, [
    resourceType,
    resourceId,
    access?.organizationId,
    access?.type,
    user?.username,
  ]);

  // Efeito responsável por abrir/fechar a conexão conforme o ciclo de vida do componente
  useEffect(() => {
    // Evita tentar conectar com parâmetros ausentes
    if (!resourceType || !resourceId) {
      setTasks([]);
      setIsConnected(false);
      setError(null);
      return;
    }

    connect();

    // Limpeza sempre que mudarem os parâmetros ou no unmount
    return () => {
      try {
        controllerRef.current?.abort();
      } catch {}
      controllerRef.current = null;
      if (readerRef.current) {
        try {
          readerRef.current.cancel();
        } catch {}
        readerRef.current = null;
      }
    };
  }, [connect, resourceType, resourceId, reloadTokenRef.current]);

  // Força um novo ciclo de conexão (encerra o atual e recria)
  const refreshTasks = useCallback(() => {
    // Abortamos a conexão atual; o efeito acima cuidará de reconectar
    try {
      controllerRef.current?.abort();
    } catch {}
    controllerRef.current = null;
    if (readerRef.current) {
      try {
        readerRef.current.cancel();
      } catch {}
      readerRef.current = null;
    }
    // Incrementa o token de reload para acionar o efeito
    reloadTokenRef.current =
      (reloadTokenRef.current + 1) % Number.MAX_SAFE_INTEGER;
    // Como o ref não re-renderiza, chamamos explicitamente connect() para resposta imediata
    // e ainda garantimos que o efeito também perceberá mudanças futuras
    connect();
  }, [connect]);

  return { tasks, error, isConnected, refreshTasks };
}
